import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from './useToast';
import { UUID } from '@/types/common';

export interface Supplier {
  id: UUID;
  name: string;
  type: string;
  contact_person: string;
  phone: string;
  email?: string | null;
  gstin?: string | null;
  pan?: string | null;
  payment_terms?: string | null;
  credit_limit?: string | number | null;
  addresses?: any[] | string | null;
  bank_details?: any | null;
  status: string;
  created_at?: string;
  updated_at?: string;
}

export function useSuppliers() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSuppliers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('status', 'active')  // lowercase to match database value
        .order('name');

      if (error) throw error;

      setSuppliers(data || []);
      return data || [];
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to load suppliers');
      toast({
        title: 'Error',
        description: 'Failed to load suppliers',
        variant: 'destructive'
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const createSupplier = async (data: Partial<Supplier>) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .insert([data]);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier created successfully',
      });

      fetchSuppliers();
    } catch (err) {
      console.error('Error creating supplier:', err);
      toast({
        title: 'Error',
        description: 'Failed to create supplier',
        variant: 'destructive'
      });
    }
  };

  const updateSupplier = async (id: UUID, data: Partial<Supplier>) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .update(data)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier updated successfully',
      });

      fetchSuppliers();
      return true;
    } catch (err) {
      console.error('Error updating supplier:', err);
      toast({
        title: 'Error',
        description: 'Failed to update supplier',
        variant: 'destructive'
      });
      return false;
    }
  };

  const deleteSupplier = async (id: UUID) => {
    try {
      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier deleted successfully',
      });

      setSuppliers(suppliers.filter(supplier => supplier.id !== id));
      return true;
    } catch (err) {
      console.error('Error deleting supplier:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete supplier',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Run only on mount
  useEffect(() => {
    fetchSuppliers();
  }, []);

  return {
    suppliers,
    isLoading,
    error,
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier
  };
}
